<template>
    <div
        :class="fieldStyle"
        class="custom-template-select"
    >
        <label
            v-if="label"
            class="input__label custom-template-select-label capitalize"
        >
            {{ $t(label) }}
        </label>
        <div
            ref="selectField"
            @click="toggleOptionsDrawer"
            class="select__field custom-template-select__field"
            :class="{ 'custom-template-select--error': !!errorMessage }"
        >
            <div class="custom-template-select__field-value">
                <slot
                    :selectedOption="selectedOption"
                    name="selectedOption"
                >
                    {{ selectedOption ? selectedOption[labelKey] : '' }}
                </slot>
            </div>
            <Icon
                :class="chevronStyle"
                class="custom-template-select__field-chevron"
            />
        </div>
        <div
            v-if="errorMessage"
            class="message message--error"
        >
            <Icon
                v-if="!isNewDesign"
                size="xs"
                class="icon-warning_triangle"
            />
            <span>
                {{ errorMessage }}
            </span>
        </div>
        <div
            v-if="isOptionsDrawerOpen"
            :class="{
                'custom-template-select__drawer--hint': isHintVisible,
                'custom-template-select__drawer--display-above': dropdownPosition === 'above',
            }"
            ref="optionsDrawer"
            @mousemove="clearHint(true)"
            class="custom-template-select__drawer"
        >
            <div
                v-for="(option, index) in options"
                :ref="`option${index}`"
                :key="index"
                @click="selectNewValue(index)"
                :class="{ 'custom-template-select__drawer-option--hint': index === currentHintIndex }"
                class="custom-template-select__drawer-option"
            >
                <slot
                    :option="option"
                    name="option"
                >
                    {{ option[labelKey] }}
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        getNextOrFirstElementIndexByLetter,
        isPointInsideRect,
        isRectVerticallyContainedInOtherRect,
    } from './selectDropdownUtils';

    export default {
        name: 'CustomTemplateSelect',
        emits: [
            'valueChanged',
            'input',
            'blur',
        ],
        props: {
            value: {
                type: [
                    Object,
                    String,
                    Number,
                ],
                required: false,
                default: () => ({}),
            },
            label: {
                type: String,
                required: false,
                default: '',
            },
            options: {
                type: Array,
                required: true,
            },
            name: {
                type: String,
                required: true,
            },
            valueKey: {
                type: String,
                required: true,
            },
            labelKey: {
                type: String,
                required: true,
            },
            isDisabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            dropdownPosition: {
                type: String,
                required: false,
                default: 'below',
                validator(value) {
                    return ['above', 'below'].includes(value);
                },
            },
            variant: {
                type: String,
                required: false,
                default: 'white',
                validator(value) {
                    return ['white', 'grey'].includes(value);
                },
            },
            isNewDesign: {
                type: Boolean,
                required: false,
                default: false,
            },
            errorMessage: {
                type: String,
                required: false,
                default: '',
            },
        },
        data() {
            return {
                isOptionsDrawerOpen: false,
                currentHint: {},
                isHintVisible: false,
            };
        },
        created() {
            this.isValueUsedInsteadOfFullObject = typeof this.value !== 'object';
        },
        computed: {
            selectedOption() {
                if (this.isValueUsedInsteadOfFullObject) {
                    return this.options.find((option) => option[this.valueKey] === this.value);
                } else {
                    return this.value;
                }
            },
            chevronStyle() {
                return this.isOptionsDrawerOpen ? 'icon-chevron_up' : 'icon-chevron_down';
            },
            currentHintIndex() {
                return this.currentHint ? this.currentHint.index : -1;
            },
            fieldStyle() {
                return {
                    'custom-template-select--focus': this.isOptionsDrawerOpen,
                    'custom-template-select--disabled': this.isDisabled,
                    'custom-template-select-new': this.isNewDesign,
                    'has-label': this.label,
                    'custom-template-select--grey': this.variant === 'grey',
                };
            },
        },
        methods: {
            bindEventListeners() {
                window.addEventListener('keydown', this.selectNextLetterSearchValue);
                window.addEventListener('mousedown', this.closeDrawerOnClickOutside);
            },
            clearEventListeners() {
                window.removeEventListener('keydown', this.selectNextLetterSearchValue);
                window.removeEventListener('mousedown', this.closeDrawerOnClickOutside);
            },
            toggleOptionsDrawer() {
                if (this.isOptionsDrawerOpen) {
                    this.closeOptionsDrawer();
                } else {
                    this.openOptionsDrawer();
                }
            },
            openOptionsDrawer() {
                this.isOptionsDrawerOpen = true;
                this.bindEventListeners();

                if (this.selectedOption) {
                    // Allow options to render in newly shown drawer and highlight the currently selected one
                    this.$nextTick(() => {
                        this.setCurrentHint(
                            this.options.findIndex((option) => option.order === this.selectedOption.order),
                            this.selectedOption[this.labelKey].substr(0, 1),
                        );
                        this.showHint();
                    });
                }
            },
            closeOptionsDrawer() {
                this.isOptionsDrawerOpen = false;
                this.clearEventListeners();
            },
            showHint() {
                if (this.currentHint.domElement) {
                    this.isHintVisible = true;
                    this.scrollToOptionIfNecessary();
                }
            },
            clearHint(removeHistory = false) {
                this.isHintVisible = false;

                if (removeHistory) {
                    this.currentHint = {};
                }
            },
            closeDrawerOnClickOutside(mouseEvent) {
                const domRects = [
                    this.$refs.optionsDrawer.getBoundingClientRect(),
                    // We check click is outside this as well beause this element
                    // already has click binded to toggle and logic would ping-pong
                    this.$refs.selectField.getBoundingClientRect(),
                ];

                if (domRects.every((domRect) => !isPointInsideRect(mouseEvent, domRect))) {
                    this.closeOptionsDrawer();
                }
            },
            selectNextLetterSearchValue(event) {
                this.clearHint();

                const firstLetter = event.key;
                const hasHint = this.currentHint && this.currentHint.key === firstLetter.toUpperCase();

                // If we have last index we search for next value
                const lastIndex = hasHint ? this.currentHint.index : -1;

                const optionIndex = getNextOrFirstElementIndexByLetter(
                    this.options,
                    this.labelKey,
                    firstLetter,
                    lastIndex,
                );

                // No valid option exists
                if (optionIndex === undefined) {
                    this.currentHint = {};
                    return;
                }

                this.selectNewValue(optionIndex, false);

                this.setCurrentHint(optionIndex, firstLetter);
                this.showHint();
            },
            setCurrentHint(optionIndex, firstLetter) {
                this.currentHint = {
                    index: optionIndex,
                    key: firstLetter.toUpperCase(),
                    domElement: this.$refs[`option${optionIndex}`][0],
                };
            },
            scrollToOptionIfNecessary() {
                const hintDomElement = this.currentHint.domElement;
                const optionsDrawer = this.$refs.optionsDrawer;

                if (!this.isOptionVisible(hintDomElement)) {
                    optionsDrawer.scrollTo(0, hintDomElement.offsetTop - optionsDrawer.clientHeight * 0.5);
                }
            },
            selectNewValue(index, shouldCloseDrawer = true) {
                if (this.isValueUsedInsteadOfFullObject) {
                    this.updateValue(this.options[index][this.valueKey]);
                } else {
                    this.updateValue(this.options[index]);
                }
                this.$emit('blur', this.name, this.options[index][this.valueKey], true);

                if (shouldCloseDrawer) {
                    this.closeOptionsDrawer();
                }
            },
            updateValue(value) {
                this.$emit('valueChanged', value);
                this.$emit('input', value);
            },
            isOptionVisible(optionDomElement) {
                const drawerRect = this.$refs.optionsDrawer.getBoundingClientRect();
                const domElementRect = optionDomElement.getBoundingClientRect();

                // Add some negative offset to help center elements that are visible but near margins
                const heightMarginOffset = drawerRect.height * -0.2;

                return isRectVerticallyContainedInOtherRect(
                    domElementRect,
                    drawerRect,
                    heightMarginOffset,
                );
            },
        },
        beforeDestroy() {
            this.clearEventListeners();
        },
    };
</script>
